/**
 * JulietteSuiviList — Contenu du Suivi "promesses & actions" reutilisable.
 *
 * Sert au tiroir mobile (JulietteSuiviDrawer) ET au rail permanent desktop
 * (JulietteFullscreen, >= xl). Query + mutations + sections En retard / À faire /
 * Fait. Le chrome (panneau coulissant vs colonne fixe) est gere par le parent.
 */

import { useMemo } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Clock, AlertTriangle, CircleSlash, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  listJulietteActions,
  markActionDone,
  cancelJulietteAction,
  actionTypeLabel,
  type JulietteAction,
} from "@/lib/juliette-actions";

function formatDue(iso: string): { label: string; overdue: boolean } {
  const due = new Date(iso).getTime();
  const diffMs = due - Date.now();
  const day = 86_400_000;
  const days = Math.round(diffMs / day);
  if (diffMs < -day) return { label: `en retard de ${Math.abs(days)} j`, overdue: true };
  if (diffMs < 0) return { label: "en retard", overdue: true };
  if (days === 0) return { label: "aujourd'hui", overdue: false };
  if (days === 1) return { label: "demain", overdue: false };
  if (days < 7) return { label: `dans ${days} j`, overdue: false };
  return {
    label: new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }),
    overdue: false,
  };
}

export function JulietteSuiviList({ teamId, enabled }: { teamId: string | null; enabled: boolean }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: actions = [], isLoading } = useQuery({
    queryKey: ["juliette-actions", teamId],
    enabled: enabled && !!teamId,
    queryFn: () => listJulietteActions(),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["juliette-actions", teamId] });
  const onErr = (e: unknown) =>
    toast({ title: "Action impossible", description: e instanceof Error ? e.message : String(e), variant: "destructive" });

  const doneMutation = useMutation({ mutationFn: markActionDone, onSuccess: refresh, onError: onErr });
  const cancelMutation = useMutation({ mutationFn: cancelJulietteAction, onSuccess: refresh, onError: onErr });

  const { overdue, todo, done } = useMemo(() => {
    const now = Date.now();
    const overdue: JulietteAction[] = [];
    const todo: JulietteAction[] = [];
    const done: JulietteAction[] = [];
    for (const a of actions) {
      if (a.status === "done") done.push(a);
      else if (new Date(a.scheduledAt).getTime() < now) overdue.push(a);
      else todo.push(a);
    }
    done.sort((a, b) => (b.doneAt ?? "").localeCompare(a.doneAt ?? ""));
    return { overdue, todo, done: done.slice(0, 15) };
  }, [actions]);

  const busy = doneMutation.isPending || cancelMutation.isPending;

  return (
    <div className="flex-1 overflow-y-auto px-3 space-y-5">
      {isLoading && <p className="px-2 py-4 text-sm text-white/40">Chargement…</p>}

      {!isLoading && actions.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center px-6">
          <CheckCircle2 className="h-8 w-8 text-success/70" />
          <p className="text-sm text-white/60">Rien en attente.</p>
          <p className="text-xs text-white/35">Les promesses et relances que Juliette note apparaîtront ici.</p>
        </div>
      )}

      <Section
        title="En retard"
        icon={<AlertTriangle className="h-3.5 w-3.5 text-destructive" />}
        items={overdue}
        tone="overdue"
        busy={busy}
        onDone={(id) => doneMutation.mutate(id)}
        onCancel={(id) => cancelMutation.mutate(id)}
      />
      <Section
        title="À faire"
        icon={<Clock className="h-3.5 w-3.5 text-indigo-300" />}
        items={todo}
        tone="todo"
        busy={busy}
        onDone={(id) => doneMutation.mutate(id)}
        onCancel={(id) => cancelMutation.mutate(id)}
      />
      <Section
        title="Fait"
        icon={<CheckCircle2 className="h-3.5 w-3.5 text-success" />}
        items={done}
        tone="done"
        busy={busy}
        onDone={() => {}}
        onCancel={() => {}}
      />
    </div>
  );
}

function Section({
  title,
  icon,
  items,
  tone,
  busy,
  onDone,
  onCancel,
}: {
  title: string;
  icon: ReactNode;
  items: JulietteAction[];
  tone: "overdue" | "todo" | "done";
  busy: boolean;
  onDone: (id: string) => void;
  onCancel: (id: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 px-2 mb-1.5">
        {icon}
        <span className="text-[11px] font-semibold uppercase tracking-wide text-white/45">{title}</span>
        <span className="text-[11px] text-white/30">· {items.length}</span>
      </div>
      <div className="space-y-1.5">
        {items.map((a) => (
          <ActionRow key={a.id} action={a} tone={tone} busy={busy} onDone={onDone} onCancel={onCancel} />
        ))}
      </div>
    </div>
  );
}

function ActionRow({
  action,
  tone,
  busy,
  onDone,
  onCancel,
}: {
  action: JulietteAction;
  tone: "overdue" | "todo" | "done";
  busy: boolean;
  onDone: (id: string) => void;
  onCancel: (id: string) => void;
}) {
  const due = formatDue(action.scheduledAt);
  const isDone = tone === "done";
  return (
    <div
      className={cn(
        "rounded-xl border px-3 py-2.5",
        tone === "overdue" && "border-destructive/25 bg-destructive/[0.07]",
        tone === "todo" && "border-white/10 bg-white/[0.05]",
        tone === "done" && "border-white/5 bg-white/[0.03]",
      )}
    >
      <div className="flex items-start gap-2">
        <span className="flex-1 min-w-0">
          <span className={cn("block text-sm leading-snug", isDone ? "text-white/45 line-through" : "text-white/90")}>
            {action.description}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-white/40">
            <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-white/55">{actionTypeLabel(action.actionType)}</span>
            {action.contactName && <span>· {action.contactName}</span>}
            {!isDone && <span className={cn(due.overdue && "text-destructive/70")}>· {due.label}</span>}
          </span>
        </span>

        {!isDone && (
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              disabled={busy}
              onClick={() => onDone(action.id)}
              aria-label="Marquer comme fait"
              title="Fait"
              className="h-8 w-8 rounded-full flex items-center justify-center text-success hover:bg-success/15 disabled:opacity-40 transition-colors"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onCancel(action.id)}
              aria-label="Abandonner"
              title="Abandonner"
              className="h-8 w-8 rounded-full flex items-center justify-center text-white/40 hover:bg-white/10 hover:text-white/70 disabled:opacity-40 transition-colors"
            >
              <CircleSlash className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
